import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["lessonContentId"];
import { NetworkStatus } from '@apollo/client';
import { isAcceptanceTest } from 'hub-http/helpers/env';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import I18n from 'I18n';
import { useGetAiAccessControls } from 'academy-common-lib/features/aiControl/aiAccessQueries';
import { VideoAssetTypename } from '../../../../__generated__/chirp/com/hubspot/academy/catalog/rpc/LessonChirpService';
import { ASYNC_BULK_GET_QUIZ_ME_QUERY } from '../../../../__generated__/chirp/com/hubspot/academy/gen/ai/skills/rpc/QuizMeRpc.dfc.new';
import { useAsyncBulkGenerateQuizMeMutation, useAsyncBulkGetQuizMeLazyQuery } from '../../../../__generated__/chirp/com/hubspot/academy/gen/ai/skills/rpc/QuizMeRpc.hooks';
import { checkQuizMePollingStatus, useQuizMeSession } from '../utils/quizMeUtils';
import { useSingleVideoQuizRegeneration } from './useSingleVideoQuizRegeneration';
import { useLessonPreQuizSession } from '../../preAssessmentQuiz/hooks/useLessonPreQuiz';
import { useAllVideosCaptionsExist } from '../../video/videoHooks';
import { queryDataFromCache } from '../../../utils/DataFetchingClientCache.new';
import { handleApolloErrors } from '../../../utils/errorUtils';
const resolveAggregateStatus = (aggregateStatus, taskStatus) => {
  if (taskStatus === 'FAILED' || aggregateStatus === 'FAILED') {
    return 'FAILED';
  }
  if (taskStatus !== 'COMPLETED') {
    return taskStatus;
  }
  return aggregateStatus;
};
export const useLessonQuizMeVideoRequestParams = ({
  contents
}) => {
  const videoContentIds = useMemo(() => {
    var _contents$filter$map;
    return (_contents$filter$map = contents === null || contents === void 0 ? void 0 : contents.filter(c => c.__typename === VideoAssetTypename).map(v => v.id)) !== null && _contents$filter$map !== void 0 ? _contents$filter$map : [];
  }, [contents]);
  const {
    captionsExistMap,
    allVideosHaveCaptions,
    loading
  } = useAllVideosCaptionsExist({
    videoContentIds
  });
  const requestParams = useMemo(() => {
    if (!captionsExistMap || !contents) return undefined;
    const requests = contents.filter(content => content.__typename === VideoAssetTypename && captionsExistMap[String(content.id)] === true && !!content.reference && !!content.hubId).map(content => ({
      contentPortalId: content.hubId,
      videoId: Number(content.reference),
      outputLanguage: content.language,
      lessonContentId: Number(content.id)
    }));
    return requests.length > 0 ? requests : undefined;
  }, [captionsExistMap, contents]);
  return {
    requestParams,
    captionsExistMap,
    allVideosHaveCaptions,
    loading
  };
};
const useBulkQuizGeneration = ({
  videos,
  contentIds,
  hasExistingTasks,
  onGenerationCompleted,
  onTaskStatusUpdate,
  onAggregateStatusUpdate,
  triggerGeneration = true
}) => {
  const {
    aiAccessControls
  } = useGetAiAccessControls();
  const isGenerativeAiEnabled = !!(aiAccessControls !== null && aiAccessControls !== void 0 && aiAccessControls.generativeAiEnabled);
  const [pollCount, setPollCount] = useState(0);
  const [quizMeLoading, setQuizMeLoading] = useState(false);
  const [quizMeError, setQuizMeError] = useState();
  const contentIdsKey = contentIds.join('-');
  const prevContentIdsKeyRef = useRef(null);
  const [getQuizMeLazy, {
    data: pollData,
    loading: queryLoading,
    error: pollError,
    stopPolling,
    networkStatus
  }] = useAsyncBulkGetQuizMeLazyQuery({
    notifyOnNetworkStatusChange: true,
    pollInterval: 3000,
    onError: err => {
      if (pollCount < 2) {
        setQuizMeError(err);
        handleApolloErrors({
          error: err,
          loggingTitle: 'learningCenter.interactiveEducation.errors.asyncQuizMeGetQuizError'
        });
      }
    }
  });
  const isPolling = networkStatus === NetworkStatus.poll || queryLoading;
  const {
    quizReady,
    retryGeneration
  } = checkQuizMePollingStatus(pollData, !!pollError);
  const pollEffectRef = useRef({
    quizReady,
    retryGeneration,
    pollCount,
    onTaskStatusUpdate,
    onAggregateStatusUpdate,
    contentIds
  });
  pollEffectRef.current = {
    quizReady,
    retryGeneration,
    pollCount,
    onTaskStatusUpdate,
    onAggregateStatusUpdate,
    contentIds
  };
  useEffect(() => {
    const {
      quizReady: isQuizReady,
      retryGeneration: shouldRetry,
      pollCount: currentPollCount,
      onTaskStatusUpdate: handleTaskStatusUpdate,
      onAggregateStatusUpdate: handleAggregateStatusUpdate,
      contentIds: currentContentIds
    } = pollEffectRef.current;
    if (!isQuizReady && isPolling) {
      setPollCount(prev => prev + 1);
      setQuizMeLoading(true);
    }
    if (currentPollCount > 20 || isQuizReady || shouldRetry) {
      setQuizMeError(currentPollCount > 20 ? {
        message: I18n.text('learningCenter.interactiveEducation.errors.asyncQuizMeGetQuizError'),
        name: 'QuizGenerationError'
      } : undefined);
      stopPolling();
      setPollCount(0);
      setQuizMeLoading(false);
    }
    if (pollData) {
      const isTimedOut = currentPollCount > 20;
      let aggregateStatus = 'COMPLETED';
      pollData.tasks.forEach((task, index) => {
        const contentId = currentContentIds[index];
        if (contentId === undefined) {
          return;
        }
        const effectiveStatus = isTimedOut && task.status !== 'COMPLETED' ? 'FAILED' : task.status;
        handleTaskStatusUpdate(contentId, effectiveStatus);
        aggregateStatus = resolveAggregateStatus(aggregateStatus, effectiveStatus);
      });
      handleAggregateStatusUpdate(aggregateStatus);
    }
  }, [stopPolling, pollData, pollError, isPolling]);
  const [asyncGenerateQuizMe, {
    loading: generateLoading,
    error: generateError,
    data: generateData
  }] = useAsyncBulkGenerateQuizMeMutation({
    onError: err => handleApolloErrors({
      error: err,
      loggingTitle: 'learningCenter.interactiveEducation.errors.asyncQuizGenerationError'
    }),
    onCompleted: quizData => {
      const taskIds = onGenerationCompleted(quizData.result);
      void getQuizMeLazy({
        variables: {
          taskIds
        }
      });
    }
  });
  const handleTriggerGeneration = useCallback(() => {
    if (videos && !isAcceptanceTest()) {
      void asyncGenerateQuizMe({
        variables: {
          videos
        }
      });
    }
  }, [asyncGenerateQuizMe, videos]);
  const shouldTriggerQuizGeneration = useMemo(() => triggerGeneration && !hasExistingTasks && !generateLoading && !generateError && !generateData, [generateData, generateError, generateLoading, hasExistingTasks, triggerGeneration]);
  useEffect(() => {
    if ((shouldTriggerQuizGeneration || retryGeneration) && isGenerativeAiEnabled) {
      handleTriggerGeneration();
    }
  }, [handleTriggerGeneration, isGenerativeAiEnabled, retryGeneration, shouldTriggerQuizGeneration]);
  useEffect(() => {
    const prevKey = prevContentIdsKeyRef.current;
    const isInitialLoad = prevKey === null;
    prevContentIdsKeyRef.current = contentIdsKey;
    if (isInitialLoad || contentIdsKey === '' || contentIdsKey === prevKey) {
      return;
    }
    setPollCount(0);
    setQuizMeLoading(false);
    setQuizMeError(undefined);
    const isHandledByGenerationEffect = (shouldTriggerQuizGeneration || retryGeneration) && isGenerativeAiEnabled;
    if (!isHandledByGenerationEffect && isGenerativeAiEnabled && !hasExistingTasks) {
      handleTriggerGeneration();
    }
  }, [contentIdsKey, handleTriggerGeneration, hasExistingTasks, isGenerativeAiEnabled, retryGeneration, shouldTriggerQuizGeneration]);
  return {
    generateQuizLoading: generateLoading || quizMeLoading || queryLoading,
    generateQuizError: !!(generateError || quizMeError),
    quizReady,
    retryGeneration,
    data: pollData,
    regenerate: handleTriggerGeneration
  };
};
export const useLessonQuizMe = ({
  lesson
}) => {
  const {
    requestParams,
    captionsExistMap
  } = useLessonQuizMeVideoRequestParams({
    contents: lesson === null || lesson === void 0 ? void 0 : lesson.content
  });
  const {
    getQuizState,
    initialiseQuiz,
    setQuizMeTasks,
    setQuizMeStatus
  } = useQuizMeSession();
  const {
    initializeLessonPreQuiz,
    setPreQuizTasks,
    setPreQuizStatus
  } = useLessonPreQuizSession();
  const contentIds = useMemo(() => (requestParams === null || requestParams === void 0 ? void 0 : requestParams.map(request => request.lessonContentId).filter(id => id !== undefined)) || [], [requestParams]);
  const hasExistingTasks = useMemo(() => {
    if (contentIds.length === 0) {
      return false;
    }
    const cachedTaskIds = contentIds.reduce((acc, id) => {
      if (acc === null) {
        return null;
      }
      const state = getQuizState(id, 'LESSON');
      if (!(state !== null && state !== void 0 && state.tasks) || state.quizMeStatus !== 'COMPLETED') {
        return null;
      }
      acc.push(state.tasks);
      return acc;
    }, []);
    if (cachedTaskIds === null) {
      return false;
    }
    const cached = queryDataFromCache(ASYNC_BULK_GET_QUIZ_ME_QUERY, {
      taskIds: cachedTaskIds
    });
    return !!(cached !== null && cached !== void 0 && cached.tasks) && cached.tasks.length === cachedTaskIds.length && cached.tasks.every(task => task.status === 'COMPLETED');
  }, [contentIds, getQuizState]);
  const updateQuizMeGeneration = useCallback((videoContentId, taskId) => setQuizMeTasks({
    contentType: 'LESSON',
    videoContentId,
    tasks: taskId
  }), [setQuizMeTasks]);
  const updatePreQuizGeneration = useCallback(taskMap => {
    if (!lesson) {
      return;
    }
    setPreQuizTasks({
      lessonId: lesson.id,
      tasks: taskMap
    });
    setPreQuizStatus({
      lessonId: lesson.id,
      status: 'PROCESSING'
    });
  }, [lesson, setPreQuizStatus, setPreQuizTasks]);
  const onGenerationCompleted = useCallback(data => {
    if (!requestParams || !lesson) {
      return [];
    }
    requestParams.forEach(request => {
      initialiseQuiz({
        contentType: 'LESSON',
        contentId: request.lessonContentId
      });
      setQuizMeStatus({
        contentType: 'LESSON',
        contentId: request.lessonContentId,
        quizMeStatus: 'PROCESSING'
      });
    });
    initializeLessonPreQuiz({
      lessonId: lesson.id,
      totalQuestions: 0,
      generationStatus: 'PENDING'
    });
    const taskIds = [];
    const preQuizTasks = {};
    data.tasks.forEach((task, index) => {
      const videoContentId = requestParams[index].lessonContentId;
      updateQuizMeGeneration(videoContentId, task.taskId);
      preQuizTasks[videoContentId] = task.taskId;
      taskIds.push(task.taskId);
    });
    updatePreQuizGeneration(preQuizTasks);
    return taskIds;
  }, [initializeLessonPreQuiz, initialiseQuiz, lesson, requestParams, setQuizMeStatus, updateQuizMeGeneration, updatePreQuizGeneration]);
  const onTaskStatusUpdate = useCallback((contentId, status) => setQuizMeStatus({
    contentType: 'LESSON',
    contentId,
    quizMeStatus: status
  }), [setQuizMeStatus]);
  const onAggregateStatusUpdate = useCallback(status => {
    if (lesson) {
      setPreQuizStatus({
        lessonId: lesson.id,
        status
      });
    }
  }, [lesson, setPreQuizStatus]);
  const {
    generateQuizLoading,
    generateQuizError,
    quizReady,
    retryGeneration,
    data,
    regenerate
  } = useBulkQuizGeneration({
    videos: requestParams,
    contentIds,
    hasExistingTasks,
    onGenerationCompleted,
    onTaskStatusUpdate,
    onAggregateStatusUpdate
  });
  const getAllTaskIds = useCallback(() => {
    const taskIds = contentIds.reduce((acc, id) => {
      var _getQuizState;
      const task = (_getQuizState = getQuizState(id, 'LESSON')) === null || _getQuizState === void 0 ? void 0 : _getQuizState.tasks;
      if (task) acc.push(task);
      return acc;
    }, []);
    return {
      taskIds,
      contentIds
    };
  }, [contentIds, getQuizState]);
  const {
    regenerateSingleVideo
  } = useSingleVideoQuizRegeneration({
    contentType: 'LESSON',
    getAllTaskIds
  });
  const regenerateSingleVideoQuiz = useCallback(contentId => {
    if (!requestParams) {
      return;
    }
    const request = requestParams.find(r => r.lessonContentId === contentId);
    if (!request) {
      return;
    }
    const videoRequest = _objectWithoutPropertiesLoose(request, _excluded);
    regenerateSingleVideo({
      videoRequest,
      contentId
    });
  }, [requestParams, regenerateSingleVideo]);
  return {
    captionsExistMap,
    generateQuizLoading,
    generateQuizError,
    quizReady,
    retryGeneration,
    data,
    regenerate,
    regenerateSingleVideoQuiz
  };
};